import jwtDecode, { JwtPayload } from 'jwt-decode';
import dayjs, { Dayjs } from 'dayjs';
import { Logger, ChargifyLogger } from '@chargify/logger';

import {
  SessionManagerParameters,
  SessionState,
  SessionSubscriber,
  SessionExpirationResponse,
} from './types';

/**
 * Class representing the session manager.
 */
class SessionManager {
  private accessTokenUrl: string;
  private onAuthenticationRequest: () => {};
  private onAuthenticationRequestAsync: SessionManagerParameters['onAuthenticationRequestAsync'];
  private token: string;
  private expDate?: Dayjs;
  private iatDate?: Dayjs;
  private tokenLifespanInMilliseconds: number | null = null;
  private tokenRefreshIntervalHandle: ReturnType<typeof setTimeout> = null;
  private tokenCheckIntervalHandle: ReturnType<typeof setTimeout> = null;
  private intervalInMilliseconds: number;
  private failedAttemptsNumber = 0;
  private maxRetryNumber = 0;
  private tokenValid = false;
  private managerInitialized = false;
  private managerInitializing = false;
  private logger: ChargifyLogger = null;
  private subscriptions: SessionSubscriber[] = [];
  private apiUrl?: string;

  /**
   * Creates the session manager.
   * @param {string} accessTokenUrl - url on which request for token will be made.
   * @param {function} onAuthenticationRequest - function used to set custom headers on token request.
   * @param {function} onAuthenticationRequestAsync - function that resolves custom headers on token request.
   * @param {number} intervalInMilliseconds - interval time to check token validity.
   * @param {number} maxRetryNumber - max retry number on failed token request.
   */
  constructor({
    accessTokenUrl,
    onAuthenticationRequest,
    onAuthenticationRequestAsync,
    intervalInMilliseconds = 5000,
    maxRetryNumber = 3,
    apiUrl,
  }: SessionManagerParameters) {
    this.accessTokenUrl = accessTokenUrl;
    this.onAuthenticationRequest = onAuthenticationRequest;
    this.onAuthenticationRequestAsync = onAuthenticationRequestAsync;
    this.intervalInMilliseconds = intervalInMilliseconds;
    this.maxRetryNumber = maxRetryNumber;
    this.logger = Logger.get('@session-manager');
    this.apiUrl = apiUrl;
  }

  /**
   * Initializes session manager and prevents simultaneous authentication calls
   * on multiple initializations at the same time.
   */
  public initialize() {
    if (!this.managerInitializing && !this.managerInitialized) {
      this.managerInitializing = true;
      this.logger.info('Initializing session manager');
      this.authenticateUser();
    }
  }

  /**
   * Sets request headers and fetches the token.
   * Retries the token requests after error response based on max retry number and checks the token validity.
   */
  private async authenticateUser() {
    let headers = {
      'Content-Type': 'application/json',
    };
    if (this.onAuthenticationRequest) {
      const userHeaders = this.onAuthenticationRequest();
      headers = {
        ...headers,
        ...userHeaders,
      };
    }

    if (this.onAuthenticationRequestAsync) {
      const userHeaders = await this.onAuthenticationRequestAsync();
      headers = {
        ...headers,
        ...userHeaders,
      };
    }

    this.logger.info('Performing request to obtain JWT access token');

    fetch(this.accessTokenUrl, {
      method: 'POST',
      headers: headers,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not OK');
        }
        return response.json();
      })
      .then(async (data) => {
        this.token = data.token;
        const decoded = jwtDecode<JwtPayload>(data.token);
        this.logger.info('JWT access token successfuly obtained');
        await this.handleDecodedToken(decoded);

        this.tokenValid = true;
        this.failedAttemptsNumber = 0;
        this.managerInitialized = true;
        this.managerInitializing = false;

        this.tokenRefreshIntervalHandle = this.checkTokenRefresh();
        this.notifySubscribers();
      })
      .catch((error: unknown) => {
        const isTokenLifespanError =
          error instanceof Error &&
          (error.message === 'Failed to fetch token lifespan' ||
            error.message === 'API URL is not set');

        this.failedAttemptsNumber += 1;

        if (
          this.failedAttemptsNumber < this.maxRetryNumber &&
          !isTokenLifespanError
        ) {
          this.logger.warn(
            'There was a problem with obtaining JWT access token'
          );
          return this.authenticateUser();
        }
        if (this.expDate) {
          this.tokenCheckIntervalHandle = setInterval(() => {
            const timeLeft = this.expDate!.diff(dayjs());
            if (timeLeft <= 0) {
              this.tokenValid = false;
              this.notifySubscribers();
              clearInterval(this.tokenCheckIntervalHandle);
            }
          }, this.intervalInMilliseconds);
        }
      });
  }

  /**
   * Notifies subscribers about session state update
   */
  private notifySubscribers() {
    this.subscriptions.forEach(({ callback }) =>
      callback({
        isInitialized: this.isManagerInitialized,
        isTokenValid: this.isTokenValid,
      })
    );
  }

  /**
   * Refreshes the token halfway through its lifetime
   */
  public checkTokenRefresh() {
    return setInterval(() => {
      if (this.expDate && this.tokenLifespanInMilliseconds) {
        const timeLeft = this.expDate.diff(dayjs());
        if (timeLeft < this.tokenLifespanInMilliseconds / 2) {
          if (this.tokenRefreshIntervalHandle != null) {
            clearInterval(this.tokenRefreshIntervalHandle);
          }
          this.authenticateUser();
        }
      } else {
        this.logger.warn(
          'Token expiration date not set, cannot authenticate user.'
        );
      }
    }, this.intervalInMilliseconds);
  }

  private async handleDecodedToken(decodedToken: JwtPayload) {
    this.iatDate = dayjs.unix(decodedToken.iat!);
    if (decodedToken.exp) {
      this.expDate = dayjs.unix(decodedToken.exp);
      this.tokenLifespanInMilliseconds = this.expDate.diff(this.iatDate);
    } else {
      if (this.tokenLifespanInMilliseconds) {
        this.expDate = this.iatDate
          .clone()
          .add(this.tokenLifespanInMilliseconds / 1000, 'seconds');
      } else {
        const maxTokenExpirationSeconds = await this.getTokenLifespan();
        this.tokenLifespanInMilliseconds = maxTokenExpirationSeconds * 1000;
        this.expDate = this.iatDate.add(maxTokenExpirationSeconds, 'seconds');
      }
    }
  }

  /**
   * Fetches the token expiration time
   */
  private async getTokenLifespan() {
    if (!this.apiUrl) {
      this.logger.error('API URL is not set. Cannot fetch token lifespan.');
      throw new Error('API URL is not set');
    }
    try {
      const response = await fetch(`${this.apiUrl}/session-config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
      });
      const json = (await response.json()) as SessionExpirationResponse;
      return json.max_token_expiration_seconds;
    } catch {
      throw new Error('Failed to fetch token lifespan');
    }
  }

  /**
   * Clears running intervals
   */
  public destroy() {
    clearInterval(this.tokenRefreshIntervalHandle);
    clearInterval(this.tokenCheckIntervalHandle);
  }

  /**
   * Subscribe to session state updates
   */
  public subscribe(callback: (session: SessionState) => void) {
    const subscriptionId = this.subscriptions.length++;
    this.subscriptions.push({
      id: subscriptionId,
      callback,
    });

    return () => {
      this.subscriptions = this.subscriptions.filter(
        ({ id }) => id !== subscriptionId
      );
    };
  }

  /**
   * Gets the access token.
   * @return {string} The access token.
   */
  public get accessToken() {
    return this.token;
  }

  /**
   * Gets token validity.
   * @return {boolean} The token validity.
   */
  public get isTokenValid() {
    return this.tokenValid;
  }

  /**
   * Gets manager initialization state.
   * @return {boolean} The manager initialization state.
   */
  public get isManagerInitialized() {
    return this.managerInitialized;
  }
}

export default SessionManager;
